import { Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { Player } from '../Player';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Bounce Pad Behavior – launches the player upward when touched.
 * Plays a squish → spring animation on the mesh scale.
 *
 * Config fields:
 *   geyserForce  – upward launch force (reused field, default 25)
 *   speed        – visual pulse speed (default 1)
 */
export class ObstacleBouncePadBehavior extends ObstacleBehavior {
  private time: number = 0;
  private squishTime: number = -1; // -1 = not squishing
  private squishDuration: number = 0.3;
  private baseScaleY: number = 1;
  private bouncing: boolean = false;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleBouncePad', startPosition, pathIndex, config);
  }

  attach(mesh: import('@babylonjs/core').AbstractMesh): void {
    super.attach(mesh);
    this.baseScaleY = mesh.scaling.y;
  }

  initLevel(level: Level): ObstacleBouncePadBehavior {
    super.initLevel(level);
    this.level.game.sound.spatial('jump', this.mesh);
    return this;
  }

  update(deltaTime: number): void {
    this.time += deltaTime * (this.config.speed ?? 1);

    if (this.squishTime >= 0) {
      // Squish-spring animation
      this.squishTime += deltaTime;
      const t = Math.min(this.squishTime / this.squishDuration, 1);
      // Phase 1 (0→0.4): squish down to 0.4
      // Phase 2 (0.4→1): spring back with overshoot
      let scaleY: number;
      if (t < 0.4) {
        scaleY = this.baseScaleY * (1 - 0.6 * (t / 0.4));
      } else {
        const t2 = (t - 0.4) / 0.6;
        // Simple spring overshoot via sine
        scaleY = this.baseScaleY * (0.4 + 0.6 * (1 + 0.2 * Math.sin(t2 * Math.PI * 2.5) * (1 - t2)));

        const launchForce = this.config.geyserForce ?? 25;
        this.level.player.applyUpwardForce(launchForce);
        this.level?.game.sound.play('jump', this.mesh);
      }
      this.mesh.scaling.y = scaleY;

      if (t >= 1) {
        this.mesh.scaling.y = this.baseScaleY;
        this.squishTime = -1;
        this.bouncing = false;
      }
    } else {
      // Gentle idle pulse (±5% on Y)
      const pulse = 1 + 0.05 * Math.sin(this.time * 2);
      this.mesh.scaling.y = this.baseScaleY * pulse;
    }
  }

  /** Called when player lands on bounce pad */
  override playerCollision(player: Player): void {
    if (this.bouncing) return;

    // Trigger squish
    this.bouncing = true;
    this.squishTime = 0;
  }

  clone(): ObstacleBouncePadBehavior {
    return new ObstacleBouncePadBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
